// Imports
import { darken, lighten } from 'polished';
import styled, { css } from 'styled-components/macro';
import { useAppSelector } from '../../../shareables/foundation/front-end/redux/hooks';
import { GeneratedThemeProps } from '../../templates/template-style';
import { Link, useLocation } from 'react-router-dom';
import { GetWeatherResponse } from '../../../shareables/types/api/main/website/homepage/weather/get';
import Weather from './weather';


// Styled components
const BannerContainer = styled.div`
	max-width: 62.5rem;
	margin-left: auto;
	margin-right: auto;
	padding: 1.25rem 0;
`;

const GridContainer = styled.div<{ hasResellerPhrase: boolean }>`
	display: grid;
	grid-template-columns: 1fr;
	grid-template-rows: auto auto;
	
	${({ hasResellerPhrase }) =>
		hasResellerPhrase &&
		css`
			grid-template-columns: fit-content(80%) 1fr;
		`}
	
	@media only screen and (max-width: 48rem) {
		grid-template-columns: 1fr;
		grid-template-rows: auto auto auto;
	}
`;


// Define the accepted props
interface FontProps {
	fontFamily?: string | null;
	fontSize?: number | null;
}

const CommunityLink = styled(Link)<FontProps & { theme: GeneratedThemeProps }>`
	grid-column: 1;
	grid-row: 1;
	overflow-wrap: anywhere;
	margin-bottom: 0.3125rem;
	line-height: 1;
	text-decoration: none;
	
	font-family: ${({ fontFamily }) => fontFamily};
	font-size: ${({ fontSize }) => (fontSize ? `${fontSize / 16}rem` : '2.5rem')};
	color: ${(props) => darken(0.2, props.theme.baseColor)};
	
	img {
		max-width: 80%;
		max-height: 6.25rem;
		margin-bottom: -0.3125rem;
		
		@media only screen and (min-width: 62.5rem) {
			margin-bottom: -0.375rem;
		}
		
		@media only screen and (max-width: 37.5rem) {
			margin-bottom: -0.2188rem;
		}
	}
	
	${({ fontSize }) =>
		!fontSize &&
		css`
			@media only screen and (max-width: 37.5rem) {
				font-size: 1.5625rem;
			}
			
			@media only screen and (min-width: 37.5rem) and (max-width: 62.5rem) {
				font-size: 2.1875rem;
			}
		`}
	
	${(props) =>
		props.theme.isNatureStyle &&
		css`
			color: #fff;
			text-shadow: 0.125rem 0.125rem 0.3125rem rgba(0, 0, 0, 0.5);
		`}
`;

const TagLine = styled.div<FontProps & { theme: GeneratedThemeProps }>`
	grid-column: 1;
	grid-row: 2;
	line-height: 1;
	font-style: italic;
	font-family: ${({ fontFamily }) => fontFamily};
	font-size: ${({ fontSize }) => (fontSize ? `${fontSize / 16}rem` : '1rem')};
	
	${(props) =>
		props.theme.isNatureStyle &&
		css`
			color: #fff;
			text-shadow: 0.125rem 0.125rem 0.3125rem rgba(0, 0, 0, 0.5);
		`}
	
	${(props) =>
		props.theme.isPaper &&
		css`
			color: ${lighten(0.25, props.theme.baseColor)};
		`};
`;

const ResellerPhrase = styled.div<{ theme: GeneratedThemeProps }>`
	grid-column: 2;
	grid-row: 1 / -1;
	display: flex;
	justify-content: flex-end;
	align-items: flex-end;
	line-height: 1.42857143;
	color: ${(props) =>
		props.theme.isModernOrNatureStyle
			? darken(0.05, props.theme.baseColor)
			: lighten(props.theme.customResellerPhraseLighten ?? 0.1, props.theme.baseColor)};
	font-size: 0.8125rem;
	font-style: italic;
	text-align: right;
	
	${(props) =>
		props.theme.isNatureStyle &&
		css`
			color: #fff;
			text-shadow: 0.125rem 0.125rem 0.3125rem rgba(0, 0, 0, 0.5);
		`}
	
	@media only screen and (max-width: 48rem) {
		grid-column: 1;
		grid-row: 3;
	}
`;

const BannerPhotoContainer = styled.div<{ hasResellerPhrase: boolean } & { theme: GeneratedThemeProps }>`
	margin-top: 0.9375rem;
	padding-top: 30%;
	position: relative;
	
	${(props) =>
		props.theme.isModernOrNatureStyle &&
		css`
			padding-bottom: 0.3125rem;
		`}
	
	${({ hasResellerPhrase }) =>
		!hasResellerPhrase &&
		css`
			@media only screen and (max-width: 48rem) {
				margin-top: 1rem;
			}
		`}
`;

const BannerPhoto = styled.img<{ theme: GeneratedThemeProps }>`
	width: 100%;
	box-shadow: 0 0 0.625rem rgba(0, 0, 0, 0.3);
	position: absolute;
	top: 0;
	
	${(props) =>
		props.theme.isFreshStyle &&
		css`
			border-radius: 0.625rem;
		`}
`;


// Define the accepted props
interface Props {
	storybookWeather?: GetWeatherResponse;
	storybookWeatherError?: string;
}


// Function component
const Banner: React.FC<Props> = ({ storybookWeather, storybookWeatherError }) => {
	// Use Redux functionality
	const website = useAppSelector((state) => state.self.website);
	const displayWeather = useAppSelector((state) => state.self.homepageVisibility?.weather);
	
	
	// Use React Router functionality
	const location = useLocation();
	
	
	// Render nothing if website not loaded
	if (!website) {
		return null;
	}
	
	
	// Destructure
	const { communityName, banner } = website;
	
	
	// Return JSX
	return (
		<BannerContainer>
			<GridContainer hasResellerPhrase={Boolean(banner.resellerPhrase)}>
				<CommunityLink to='/' fontFamily={banner.communityName?.fontFamily} fontSize={banner.communityName?.fontSize}>
					{banner.logoURL ? <img src={banner.logoURL} alt={communityName} /> : communityName}
				</CommunityLink>
				
				{banner.tagLine && (
					<TagLine fontFamily={banner.tagLine.fontFamily} fontSize={banner.tagLine.fontSize}>
						{banner.tagLine.text}
					</TagLine>
				)}
				
				{banner.resellerPhrase && <ResellerPhrase>{banner.resellerPhrase}</ResellerPhrase>}
			</GridContainer>
			
			<BannerPhotoContainer hasResellerPhrase={Boolean(banner.resellerPhrase)}>
				<BannerPhoto src={banner.photoURL} alt='Banner photo' />
				
				{displayWeather && location.pathname === '/' && (
					<Weather storybookWeather={storybookWeather} storybookWeatherError={storybookWeatherError} />
				)}
			</BannerPhotoContainer>
		</BannerContainer>
	);
};

export default Banner;
